/* eslint-disable react-perf/jsx-no-new-function-as-prop */

import React from 'react';
import { connect } from 'react-redux';

import Currency from '@prisjakt/ui-currency';
import T from '@prisjakt/ui-translate';
import { AppContext } from '@prisjakt/ui-app-context';
import { noop } from '@prisjakt/ui-base';

import PencilIcon from 'mdi-react/PencilIcon';

import { View } from '../../types';
import ItemList from '../../components/item-list';
import ListItem, { ListItemProps } from '../../components/item-list/list-item';
import ListDnd from '../../components/list-dnd/list-dnd';
import EmptyList from '../../components/empty-list';
import DataContainer from '../../../../components/data';
import Loader from '../../../../components/loader';
import SortButton from '../../components/sort-button';
import actions from '../../actions/lists';
import alertActions from '../../actions/alert';
import SidebarHeaderDesktop from '../../components/sidebar-desktop/sidebar-header-desktop';
import SidebarHeaderMobile from '../../components/sidebar-mobile/sidebar-header-mobile';
import SettingsDialog from '../../components/settings-dialog';
import ConfirmRearrange from '../../components/confirm-rearrange';

import { SIDEBAR_LOCALSTORAGE_KEY, SORT_OPTIONS } from '../../constants';
import { VIEWS } from '../../components/sidebar-mobile/constants';

import listQuery from '../../list-query.graphql';

import * as S from './styles';
import * as SharedStyles from '../styles';

import { AppState } from '../../../../reducers';
import {
  Deleted,
  Mutation,
  Sort,
  Draggable,
  ToggleDraggable,
  SidebarHeaderOption,
} from '../../components/sharedTypes';
import { SidebarListQuery } from '../../../../types';

type OwnProps = {
  id: number;
  removeListItemRequest: (itemId: number, id: number) => void;
  orderListItemsRequest: (listId: number, itemIds: number[]) => void;
  createAlertRequest: (product: { productId: number }) => void;
  renameListRequest: (id: number, name: string) => void;
  closeLists: () => void;
  sortList: (sort: { name: string; value: Sort }) => void;
  selectList: (id: number | null) => void;
  onOpenView?: (view: View) => void;
  onLinkClick?: () => void;
  draggable: Draggable;
  toggleDraggable?: ToggleDraggable;
  isMobile?: boolean;
};

type StateProps = {
  deletedListItems: Deleted;
  lists: {
    sort: Sort;
    direction?: 'desc' | 'asc';
  };
  error: {
    message: string;
  } | null;
  listRefresh: number;
};

type DataProps = {
  data: SidebarListQuery;
  fetchMore: () => void;
  fetching: boolean;
  mutation: Mutation;
  refresh: () => void;
};

type Props = OwnProps & StateProps;
type State = {
  showSettings: boolean;
};

class List extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      showSettings: false,
    };
  }

  componentDidMount() {
    // Sets sort option from localstorage
    const sidebarState = localStorage.getItem(SIDEBAR_LOCALSTORAGE_KEY);
    const parsedSidebarState = sidebarState && JSON.parse(sidebarState);

    const selectedSort = parsedSidebarState && parsedSidebarState.listSort;
    return selectedSort ? this.setSortByOptionValue(selectedSort) : null;
  }

  setSortByOptionValue(sortOption: Sort) {
    return this.props.sortList({
      name: this.context.t('common', `sort.${sortOption}`),
      value: sortOption,
    });
  }

  openSettings = () => {
    this.setState({ showSettings: true });
  };

  closeSettings = () => {
    this.setState({ showSettings: false });
  };

  renderListItems(
    list: DataProps['data']['me']['list'],
    mutation: Mutation,
    refresh: () => void,
    fetchMore: DataProps['fetchMore'],
    fetching: DataProps['fetching']
  ) {
    if (!list.items) return null;

    const itemListProps = {
      listId: list.id,
      pageInfo: list.items.pageInfo,
      nodes: list.items.nodes,
      fetching,
      onClick: this.props.onLinkClick || noop,
      onFetchMore: () => {
        fetchMore();
      },
      refresh,
      mutation,
      renderEmpty: (
        <EmptyList
          text={this.context.t('lists', 'list.empty-helper-text')}
          icon={<SharedStyles.CreateListIcon size={64} />}
        />
      ),
      deleted: this.props.deletedListItems,
      createAlert: (product: { id: number } | null) =>
        product &&
        this.props.createAlertRequest({
          productId: product.id,
        }),
      onDelete: (itemId: number) => {
        this.props.removeListItemRequest(itemId, list.id);
      },
      onOrder: (listItemIds: number[], listId: number) => {
        this.props.orderListItemsRequest(listId, listItemIds);
        this.setSortByOptionValue(SORT_OPTIONS.USER_DEFINED);
      },
    };

    return list.itemsCount > 1 && this.props.draggable ? (
      <ListDnd
        hasDragHandle={Boolean(this.props.toggleDraggable)}
        render={(itemProps: ListItemProps) => (
          <ListItem item={itemProps.item} {...itemProps} />
        )}
        {...itemListProps}
      />
    ) : (
      <ItemList {...itemListProps} />
    );
  }

  goBack = () => {
    this.props.selectList(null);
    if (this.props.onOpenView) {
      this.props.onOpenView(VIEWS.LISTS);
    }
  };

  render() {
    const {
      lists,
      id,
      error,
      sortList,
      listRefresh,
      isMobile,
      onOpenView,
      draggable,
    } = this.props;
    const { t } = this.context;

    return (
      <DataContainer
        key={`list-${lists.sort}-${lists.direction}`}
        refreshKey={listRefresh}
        query={listQuery}
        renderLoader={() => <Loader fadeIn={false} />}
        variables={{
          id,
          limit: 30,
          sort: lists.sort,
          direction: lists.direction,
        }}
        // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
        render={({
          data,
          mutation,
          refresh,
          fetchMore,
          fetching,
        }: DataProps) => {
          if (!data) {
            return null;
          }

          const { list } = data.me;

          if (!list.items) return null;

          const listNotEmpty = list.itemsCount > 0;

          if (this.state.showSettings) {
            return (
              <>
                <SharedStyles.View>
                  <SettingsDialog
                    confirmLabel={t('common', 'button.save')}
                    onConfirm={(value: string) => {
                      this.props.renameListRequest(list.id, value);
                      this.closeSettings();
                    }}
                    showCancel
                    onCancel={this.closeSettings}
                    defaultValue={list.name}
                  />
                </SharedStyles.View>
              </>
            );
          }

          const options = [
            {
              title: t('lists', 'list.rename'),
              handler: this.openSettings,
            },
            list.itemsCount > 1
              ? this.props.toggleDraggable && {
                  title: t('lists', 'list.change-order'),
                  handler: this.props.toggleDraggable,
                }
              : undefined,
          ].filter(x => x) as SidebarHeaderOption[];

          return (
            <div>
              <S.ListWrapper>
                {error && (
                  <div className="column">
                    <p>{error.message || t('errors', 'internal.unknown')}</p>
                  </div>
                )}

                {isMobile ? (
                  <SidebarHeaderMobile
                    title={list.name}
                    onClickHandler={
                      onOpenView && (() => onOpenView(VIEWS.LISTS))
                    }
                    options={options}
                    settingsIcon={<PencilIcon />}
                  />
                ) : (
                  <SidebarHeaderDesktop
                    goBack={this.goBack}
                    title={list.name}
                    options={options}
                    settingsIcon={<PencilIcon />}
                  />
                )}

                {this.props.draggable && this.props.toggleDraggable && (
                  <ConfirmRearrange onClick={this.props.toggleDraggable} />
                )}

                {((!isMobile && listNotEmpty) ||
                  (isMobile && listNotEmpty && !draggable)) && (
                  <SortButton
                    sort={{
                      ...list.items.pageInfo.sort,
                      available: [
                        SORT_OPTIONS.USER_DEFINED,
                        SORT_OPTIONS.ADDED,
                        SORT_OPTIONS.NAME,
                        SORT_OPTIONS.PRICE_ASC,
                        SORT_OPTIONS.PRICE_DESC,
                        SORT_OPTIONS.CATEGORY,
                      ],
                    }}
                    onSelect={sort => sortList(sort)}
                  />
                )}

                {list &&
                  this.renderListItems(
                    list,
                    mutation,
                    refresh,
                    fetchMore,
                    fetching
                  )}
              </S.ListWrapper>

              {list.itemsCount > 0 && (
                <S.ListSummary>
                  <S.ListSummaryPrice>
                    <T
                      sum={
                        <strong>
                          <Currency price={list.priceSummary} />
                        </strong>
                      }
                      domain="lists"
                    >
                      list.price-sum
                    </T>
                  </S.ListSummaryPrice>
                </S.ListSummary>
              )}
            </div>
          );
        }}
      />
    );
  }
}

List.contextType = AppContext;

const mapStateToProps = (state: AppState) => ({
  deletedListItems: state.areas.sidebar.deletedListItems,
  lists: state.areas.sidebar.lists,
  error: state.areas.sidebar.error,
  listRefresh: state.areas.sidebar.listRefresh,
});

const dispatchActions = {
  ...actions,
  ...alertActions,
};

export default connect(
  mapStateToProps,
  dispatchActions
)(List);
